import {
  contains,
  getRelatedTarget,
  isNumeric,
  mergeClassnames,
  useEventListener,
  useOnScrollOutside
} from '@mote/helpers'
import { preventBubblingEvent } from '@mote/helpers/browser'
import { KeyboardKey, noop } from '@mote/helpers/constants'
import React from 'react'

export const TRANSITION_DURATION = 150

export const usePanel = props => {
  const {
    id,
    children: _children,
    panelClassName,
    menuClassName,
    triggerClassName,
    finalFocusRef,
    variant = 'light',
    offCanvas = false,
    position = 'center',
    activeItemIndex = -1,
    returnFocusOnClose = true,
    focusItemOnHover = false,
    disabled = false,
    onLevelChange = noop,
    'aria-label': ariaLabel,
    'aria-labelledby': ariaLabelledBy,
    'aria-controls': ariaControls,
    ...rest
  } = props

  const [level, setLevel] = React.useState(0)
  const [isOpen, setIsOpen] = React.useState(false)
  const activeItemIndexRef = React.useRef(activeItemIndex)
  const menuItems = React.useRef([])
  const panelRef = React.useRef(null)
  const menuRef = React.useRef(null)
  const triggerRef = React.useRef(null)
  const timeoutIds = React.useRef({
    trigger: null,
    menu: null
  })

  React.useEffect(() => {
    if (activeItemIndex === activeItemIndexRef.current) return

    activeItemIndexRef.current = activeItemIndex
  }, [activeItemIndex])

  const goUp = React.useCallback(() => {
    setLevel(prev => prev - 1)
  }, [])

  const goDown = React.useCallback(() => {
    setLevel(prev => prev + 1)
  }, [])

  const focusTarget = React.useCallback(key => {
    if (!key) return
    const target = key === 'trigger' ? triggerRef : menuRef

    timeoutIds.current[key] = setTimeout(() => {
      target.current?.focus()
    }, TRANSITION_DURATION)
  }, [])

  const populateItems = React.useCallback(() => {
    if (disabled) return
    if (!menuRef.current) return

    menuItems.current = Array.from(
      menuRef.current.querySelectorAll('[role="menuitem"]')
    )
  }, [disabled])

  const hidePanel = React.useCallback(
    event => {
      if (!isOpen) return

      setIsOpen(false)
      setLevel(0)
      activeItemIndexRef.current = -1

      if (!returnFocusOnClose && finalFocusRef?.current) {
        finalFocusRef.current.focus()
        return
      }

      if (returnFocusOnClose && !finalFocusRef?.current) focusTarget('trigger')
    },
    [finalFocusRef, returnFocusOnClose, isOpen]
  )

  const showPanel = React.useCallback(
    (event, options = { focusIndex: 0 }) => {
      preventBubblingEvent(event)
      if (disabled) return

      if (isOpen) {
        hidePanel(event)
        return
      }

      if (activeItemIndexRef.current === -1)
        activeItemIndexRef.current = options.focusIndex

      focusTarget('menu')

      setIsOpen(true)
    },
    [disabled, isOpen, hidePanel]
  )

  const outSideEventHandler = React.useCallback(
    event => {
      if (!isOpen || triggerRef.current?.contains(event.target)) return

      hidePanel(event)
    },
    [isOpen, hidePanel]
  )

  const handleOnItemClick = React.useCallback(
    (event, onClick) => {
      if (disabled) return

      onClick?.(event, activeItemIndexRef.current)
      hidePanel(event)
    },
    [disabled, hidePanel]
  )

  const handleOnItemKeyDown = React.useCallback(
    event => {
      if (disabled) return

      if (event.shiftKey && event.keyCode === KeyboardKey.Tab) {
        preventBubblingEvent(event)
        hidePanel(event)
        return
      }

      const items = menuItems.current
      let index = 0
      let changedIndex = false

      const currentIndex = items.indexOf(document.activeElement)

      if (currentIndex === -1) return

      const hasSubmenu = Boolean(
        document.activeElement.getAttribute('aria-haspopup')
      )

      switch (event.keyCode) {
        case KeyboardKey.Escape:
          preventBubblingEvent(event)
          hidePanel(event)
          return
        case KeyboardKey.Tab:
          setIsOpen(false)
          activeItemIndexRef.current = -1
          return
        case KeyboardKey.Space:
        case KeyboardKey.Enter:
          preventBubblingEvent(event)
          items[currentIndex].click()
          return
        case KeyboardKey.UpArrow:
          preventBubblingEvent(event)
          index = Math.max(0, currentIndex - 1)
          changedIndex = true
          break
        case KeyboardKey.RightArrow:
          preventBubblingEvent(event)
          if (!hasSubmenu) return
          items[currentIndex].click()
          return
        case KeyboardKey.DownArrow:
          preventBubblingEvent(event)
          index = Math.min(items.length - 1, currentIndex + 1)
          changedIndex = true
          break
        case KeyboardKey.Home:
          preventBubblingEvent(event)
          index = 0
          changedIndex = true
          break
        case KeyboardKey.End:
          preventBubblingEvent(event)
          index = items.length - 1
          changedIndex = true
          break
        default:
          break
      }

      if (changedIndex && index !== currentIndex) {
        items[index].focus()
        activeItemIndexRef.current = index
      }
    },
    [disabled, hidePanel, menuItems.current]
  )

  const handleOnTriggerKeyDown = React.useCallback(
    event => {
      if (disabled) return

      switch (event.keyCode) {
        case KeyboardKey.Escape:
          preventBubblingEvent(event)

          if (!isOpen) return
          hidePanel(event)
          break
        case KeyboardKey.Enter:
        case KeyboardKey.Space:
          preventBubblingEvent(event)

          showPanel(event, { focusIndex: 0 })
          break
        default:
          break
      }
    },
    [disabled, isOpen, showPanel, hidePanel]
  )

  const handleOnPanelKeyDown = React.useCallback(
    event => {
      if (disabled || !isOpen) return

      const isActiveElement = document.activeElement === panelRef.current
      const level = panelRef.current.dataset.level
      const isSubmenu = isNumeric(level) && level > 0

      switch (event.keyCode) {
        case KeyboardKey.Escape:
          preventBubblingEvent(event)
          hidePanel(event)
          break
        case KeyboardKey.LeftArrow:
          preventBubblingEvent(event)
          if (!isSubmenu) return
          goUp()
          break
        case KeyboardKey.RightArrow:
        case KeyboardKey.UpArrow:
        case KeyboardKey.DownArrow:
          if (!isActiveElement) return
          preventBubblingEvent(event)
          break
        default:
          break
      }
    },
    [disabled, isOpen, hidePanel, goUp]
  )

  const handleOnSpaceKeyUp = React.useCallback(
    event => {
      if (disabled) return

      switch (event.keyCode) {
        case KeyboardKey.Space:
          event.preventDefault()
          break
        default:
          break
      }
    },
    [disabled]
  )

  const handleOnItemMouseOver = React.useCallback(
    event => {
      const currentIndex = menuItems.current.indexOf(event.currentTarget)

      if (currentIndex !== -1) {
        activeItemIndexRef.current = currentIndex
      }

      if (disabled || !focusItemOnHover) return
      if (currentIndex === -1) return

      event.currentTarget?.focus()
    },
    [focusItemOnHover, disabled]
  )

  const handleMenuFocus = React.useCallback(() => {
    if (menuItems.current.length === 0) return
    if (activeItemIndexRef.current === -1) return

    menuItems.current[activeItemIndexRef.current].focus()
  }, [menuItems.current])

  const handlePanelBlur = React.useCallback(
    event => {
      const relatedTarget = getRelatedTarget(event)
      const targetIsMenu = contains(panelRef.current, relatedTarget)
      const targetIsTrigger = contains(triggerRef.current, relatedTarget)
      const isValidBlur = !targetIsMenu && !targetIsTrigger

      if (isOpen && isValidBlur) {
        hidePanel(event)
      }
    },
    [isOpen, hidePanel]
  )

  useEventListener('focus', handleMenuFocus, menuRef)
  useOnScrollOutside(panelRef, outSideEventHandler)

  React.useEffect(() => {
    onLevelChange(level)
    activeItemIndexRef.current = 0
  }, [level])

  React.useEffect(() => {
    populateItems()

    const isPanelVisible =
      panelRef.current?.getAttribute('aria-hidden') === 'false'

    if (isPanelVisible) focusTarget('menu')
  }, [_children])

  React.useEffect(() => {
    populateItems()

    return () => {
      Object.values(timeoutIds.current).forEach(id => {
        clearTimeout(id)
      })

      timeoutIds.current = {}
    }
  }, [])

  const childDefaultProps = React.useMemo(
    () => ({
      role: 'menuitem',
      tabIndex: -1,
      onKeyDown: handleOnItemKeyDown,
      onKeyUp: handleOnSpaceKeyUp,
      onMouseOver: handleOnItemMouseOver
    }),
    [handleOnItemKeyDown, handleOnItemMouseOver, handleOnSpaceKeyUp]
  )

  const children = React.useMemo(
    () =>
      React.Children.map(_children, (child, index) => {
        if (!React.isValidElement(child)) {
          if (child !== null)
            console.error('Children has invalid elements', { child })
          return child
        }

        const { hasSubmenu, className, onClick, ...rest } = child.props

        const childProps = {
          ...childDefaultProps,
          ...rest,
          className: mergeClassnames('panel-item', className),
          'aria-haspopup': hasSubmenu ? true : null,
          onClick: event => {
            if (hasSubmenu) {
              goDown()

              return
            }

            handleOnItemClick(event, onClick)
          }
        }

        return React.cloneElement(child, childProps)
      }).filter(Boolean),
    [_children, childDefaultProps, goDown, handleOnItemClick]
  )

  const triggerProps = React.useMemo(() => {
    const props = {
      disabled,
      ref: triggerRef,
      className: mergeClassnames('panel-trigger', triggerClassName),
      type: 'button',
      onClick: showPanel,
      onKeyDown: handleOnTriggerKeyDown,
      onKeyUp: handleOnSpaceKeyUp,
      'aria-label': ariaLabel,
      'aria-disabled': disabled,
      'aria-expanded': isOpen,
      'aria-haspopup': true,
      'aria-controls': ariaControls ?? id
    }

    return props
  }, [
    triggerRef,
    triggerClassName,
    ariaLabel,
    disabled,
    isOpen,
    ariaControls,
    id,
    showPanel,
    handleOnTriggerKeyDown,
    handleOnSpaceKeyUp
  ])

  const menuProps = React.useMemo(() => {
    const props = {
      variant,
      className: mergeClassnames('panel-menu', menuClassName),
      ref: menuRef,
      tabIndex: -1,
      role: 'menu',
      'aria-orientation': 'vertical'
    }

    return props
  }, [menuClassName, variant])

  const panelProps = React.useMemo(() => {
    const props = {
      id,
      disabled,
      isOpen,
      variant,
      offCanvas,
      position,
      className: mergeClassnames('panel', panelClassName),
      ref: panelRef,
      ...rest,
      onKeyDown: handleOnPanelKeyDown,
      onBlur: handlePanelBlur,
      tabIndex: -1,
      role: 'menu',
      'data-level': level,
      'aria-hidden': !isOpen,
      'aria-labelledby': !ariaLabel ? ariaLabelledBy : null,
      'aria-orientation': 'vertical'
    }

    return props
  }, [
    variant,
    offCanvas,
    position,
    level,
    id,
    panelClassName,
    disabled,
    isOpen,
    rest,
    ariaLabel,
    ariaLabelledBy,
    handleOnPanelKeyDown,
    handlePanelBlur
  ])

  return {
    panelProps,
    menuProps,
    triggerProps,
    children,
    isOpen,
    level,
    offCanvas,
    variant,
    showPanel,
    hidePanel,
    goUp,
    goDown
  }
}
